// Generic function to filter options by any property
export function getFilteredOptions(
  options: any[],
  filterOptions: any[],
  propertyName: string,
  propertyValue: string
) {
  if (!propertyValue || !options || propertyValue.startsWith("No")) {
    return [];
  }

  if (filterOptions.length <= 1) {
    return options;
  }

  return options
    .map((group: any) => {
      if (Array.isArray(group.options)) {
        const filtered = group.options.filter(
          (option: any) => option[propertyName] === propertyValue
        );
        return { ...group, options: filtered };
      }

      return group;
    })
    .filter((group: any) => {
      if (Array.isArray(group.options)) {
        return group.options.length > 0;
      }
      return true;
    });
}

/**
 * Parses a composite entity key into its components
 * Format: "name||code||registrationNumber" or "name||code"
 */
const parseEntityCompositeKey = (compositeKey: string) => {
  if (!compositeKey || !compositeKey.includes("||")) {
    return { name: compositeKey, code: "", registrationNumber: "" };
  }

  const parts = compositeKey.split("||");
  return {
    name: parts[0] || "",
    code: parts[1] || "",
    registrationNumber: parts[2] || "",
  };
};

/**
 * Parses entity code format: "code" or "code - registration number"
 */
const parseEntityCode = (entityCode: string) => {
  const parts = entityCode.split(" - ");
  return {
    code: parts[0],
    registrationNumber: parts[1], // undefined if not present
  };
};

/**
 * Builds a composite entity key from components
 * Accepts entityCode which may be in format "code" or "code - registration number"
 * and parses it internally before building the composite key
 */
export const buildEntityCompositeKey = (name: string, entityCode: string) => {
  const { code, registrationNumber } = parseEntityCode(entityCode);
  return registrationNumber
    ? `${name}||${code}||${registrationNumber}`
    : `${name}||${code}`;
};

/**
 * Extracts entity code from composite key
 */
export const getEntityCodeFromCompositeKey = (compositeKey: string) => {
  const { code, registrationNumber } = parseEntityCompositeKey(compositeKey);
  return registrationNumber ? `${code} - ${registrationNumber}` : code;
};

/**
 * Extracts entity name from composite key
 */
export const getEntityNameFromEntityCompositeKey = (compositeKey: string) => {
  const { name } = parseEntityCompositeKey(compositeKey);
  return name;
};

// Make sure the Ui content is not the dummy value.
export const hasRealUiContent = (uiContent: any): boolean => {
  if (!uiContent) return false;

  const optionKeys = [
    "countryOptions",
    "entityOptions",
    "governmentEntityOptions",
  ];

  for (const key of optionKeys) {
    const options = uiContent[key];
    if (options && options.length > 0) {
      const hasDummyData = options.some(
        (option: any) =>
          option.value?.startsWith("No ") || option.label?.startsWith("No ")
      );
      if (hasDummyData) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Gets entity code from entity value (supports both composite key and entity long name)
 * extracts code directly if composite key
 * looks up by label if long name
 * NOTE: Long name (label) is not guaranteed to be unique in entityOptions!
 * This will return first match if duplicate long names exist
 */
export const getEntityCodeFromLongName = (
  entityValue: string,
  uiContent: any
) => {
  if (!entityValue || !uiContent || !uiContent.entityOptions) {
    return "";
  }

  // Check if it's a composite key format
  if (entityValue.includes("||")) {
    return getEntityCodeFromCompositeKey(entityValue);
  }

  // entity value is just the name - find by label
  const match = uiContent.entityOptions.find(
    (option: any) => option.label === entityValue
  );
  return match?.description ?? "";
};

export const getCurrencySignFromCountry = (country: string, uiContent: any) => {
  // Fallback - we can remove once global country/state uicontent is available for all teams
  const COUNTRY_TO_CURRENCY_MAPPING: Record<string, string> = {
    US: "$",
    BR: "R$",
    CA: "C$",
    IN: "₹",
    DE: "€",
    PR: "$",
    EU: "€",
    APAC: "",
  };

  const shortCountryName = getShortNameFromLong(country);

  let mapping = COUNTRY_TO_CURRENCY_MAPPING;

  if (
    uiContent &&
    uiContent.countryToCurrencyMap &&
    Object.keys(uiContent.countryToCurrencyMap).length > 0
  ) {
    mapping = uiContent.countryToCurrencyMap;
  }

  return mapping[shortCountryName] ?? "$";
};

const longToShortMapping = {
  "United States": "US",
  Brazil: "BR",
  Canada: "CA",
  India: "IN",
  Germany: "DE",
  "Puerto Rico": "PR",
  "European Union": "EU",
  "Asia-Pacific": "APAC",
};

export const getShortNameFromLong = (name: string): string => {
  return longToShortMapping[name as keyof typeof longToShortMapping] || name;
};

export const getLongNameFromShort = (name: string): string => {
  const longNameEntry = Object.entries(longToShortMapping).find(
    ([longName, shortCode]) => shortCode === name
  );
  return longNameEntry ? longNameEntry[0] : name;
};
